import { useState, useEffect } from 'react';
import { Nav, Form, Button, Badge, Card, Container, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalculator,
  faExchangeAlt,
  faArchive,
  faCog,
  faPlus,
  faEdit,
  faCalendarAlt,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { useAuthContext } from '../../../contexts/AuthContext';
import { formatCurrency } from '../../../utils/formatCurrency';
import { DEFAULT_SETTINGS } from './RepairCalcConstants';
import { getRepairTypeLabel, getProblemsFromRepairType } from './repairTypes';
import Calculator from './Calculator';
import ReverseCalculator from './ReverseCalculator';
import SettingsView from './SettingsView';
import RepairCalcArchive from './RepairCalcArchive';
import './RepairCalc.scss';

const TABS = [
  { key: 'calc', icon: faCalculator },
  { key: 'reverse', icon: faExchangeAlt },
  { key: 'archive', icon: faArchive },
  { key: 'settings', icon: faCog }
];

export default function RepairCalcApp() {
  const { t } = useTranslation();
  const { currentUser: user } = useAuthContext();
  const navigate = useNavigate();

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [brandInput, setBrandInput] = useState('');
  const [modelInput, setModelInput] = useState('');
  const [existingQuotes, setExistingQuotes] = useState([]);
  const [quoteToEdit, setQuoteToEdit] = useState(null);
  const [showCalculator, setShowCalculator] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [currentTab, setCurrentTab] = useState('calc');
  const [savingStatus, setSavingStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'

  const brand = brandInput.trim();
  const model = modelInput.trim();

  const settingsDocRef = () => doc(db, 'users', user.uid, 'repairCalcSettings', 'default');
  const quotesCollectionRef = () => collection(db, 'users', user.uid, 'quotes');

  const handleSettingsChange = async (newSettings) => {
    setSettings(newSettings);
    if (!user) return;

    setSavingStatus('saving');
    try {
      const cleanSettings = {};
      Object.entries(newSettings).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          cleanSettings[key] = value;
        }
      });

      await setDoc(settingsDocRef(), { ...cleanSettings, updatedAt: new Date().toISOString() });
      setSavingStatus('saved');
      setTimeout(() => setSavingStatus('idle'), 2000);
    } catch (error) {
      console.error(error);
      setSavingStatus('error');
    }
  };

  // Carica le impostazioni iniziali
  useEffect(() => {
    async function fetchSettings() {
      if (!user) return;
      try {
        const docSnap = await getDoc(settingsDocRef());
        if (docSnap.exists()) {
          setSettings({ ...DEFAULT_SETTINGS, ...docSnap.data() });
        }
      } catch (error) {
        // ignora silenziosamente: restano i default
      }
    }
    fetchSettings();
  }, [user]);

  // Carica i preventivi esistenti per marca + modello (con debounce)
  useEffect(() => {
    if (!brand || !model || !user) {
      setExistingQuotes([]);
      setQuoteToEdit(null);
      setShowCalculator(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const snaps = await getDocs(collection(db, 'users', user.uid, 'quotes'));
        const results = [];
        snaps.forEach((snap) => {
          const data = snap.data();
          if (data.brand === brand && data.modelName === model) {
            results.push({ id: snap.id, ...data });
          }
        });
        results.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

        if (results.length > 0) {
          setExistingQuotes(results);
          setQuoteToEdit(null);
          setShowCalculator(false);
        } else {
          setExistingQuotes([]);
          setQuoteToEdit(null);
          setShowCalculator(true);
        }
      } catch (e) {
        console.error(e);
        setExistingQuotes([]);
        setQuoteToEdit(null);
        setShowCalculator(true);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [brand, model, user]);

  const persistQuote = async (data) => {
    if (!user || !brand || !model) return null;
    const uniqueSuffix = data.repairType ? ` - ${data.repairType.trim()}` : '';
    const docId = encodeURIComponent(`${brand} ${model}` + uniqueSuffix);
    const quoteRef = doc(db, 'users', user.uid, 'quotes', docId);

    const payload = {
      ...data,
      brand,
      modelName: model,
      updatedAt: new Date().toISOString()
    };

    await setDoc(quoteRef, payload);
    return { docId, payload };
  };

  const handleSaveQuote = async (data) => {
    setIsSaving(true);

    try {
      const result = await persistQuote(data);
      if (!result) return;

      const newQuoteItem = { id: result.docId, ...result.payload };
      setExistingQuotes((prev) => {
        const filtered = prev.filter((q) => q.id !== result.docId);
        return [newQuoteItem, ...filtered].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      });

      setShowCalculator(false);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  // Costruisce il prefill e apre il form dispositivo (Dashboard)
  const navigateToDeviceForm = ({ brand: b, model: m, repairType, totalClient }) => {
    const { problems, customProblems } = getProblemsFromRepairType(repairType);
    const price = totalClient ? String(Math.round(totalClient * 100) / 100) : '';

    navigate('/', {
      state: {
        openDeviceForm: true,
        prefill: {
          brand: b,
          model: m,
          problems,
          customProblems,
          price
        }
      }
    });
  };

  // Salva prima il preventivo, poi importa i dati nel form dispositivo (Dashboard)
  const handleImportToDevice = async (data) => {
    setIsSaving(true);

    try {
      await persistQuote(data);
      navigateToDeviceForm({ brand, model, repairType: data.repairType, totalClient: data.totalClient });
    } catch (error) {
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  // Importa un preventivo nel form dispositivo (Dashboard)
  const handleImportQuote = (quote) => {
    navigateToDeviceForm({
      brand: quote.brand,
      model: quote.modelName,
      repairType: quote.repairType,
      totalClient: quote.totalClient
    });
  };

  const savingBadge = () => {
    if (savingStatus === 'saving') return <Badge bg="info" className="ms-2">{t('repaircalc.messages.saving')}</Badge>;
    if (savingStatus === 'saved') return <Badge bg="success" className="ms-2">{t('repaircalc.messages.saved')}</Badge>;
    if (savingStatus === 'error') return <Badge bg="danger" className="ms-2">{t('repaircalc.messages.error')}</Badge>;
    return null;
  };

  const difficultyLabel = (d) => {
    if (d === 'easy') return t('repaircalc.calculator.easy');
    if (d === 'hard') return t('repaircalc.calculator.hard');
    return t('repaircalc.calculator.medium');
  };

  return (
    <Container fluid className="repaircalc-container">
      <div className="repaircalc-header mb-3">
        <h1>{t('repaircalc.title')}</h1>
        {savingBadge()}
      </div>

      <Nav
        className="repaircalc-nav mb-4"
        activeKey={currentTab}
        onSelect={(k) => setCurrentTab(k)}
      >
        {TABS.map((tab) => (
          <Nav.Item key={tab.key}>
            <Nav.Link
              eventKey={tab.key}
              className={`nav-tab ${currentTab === tab.key ? 'active' : ''}`}
            >
              <div className="nav-tab-content">
                <div className="icon-container">
                  <FontAwesomeIcon icon={tab.icon} />
                </div>
                <span>{t(`repaircalc.tabs.${tab.key}`)}</span>
              </div>
            </Nav.Link>
          </Nav.Item>
        ))}
      </Nav>

      <div className="tab-content">
        {/* Preventivo */}
        <div className={`tab-pane repaircalc-tab-wide ${currentTab === 'calc' ? 'show active' : ''}`}>
          <div className="repaircalc-section mb-4">
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t('repaircalc.search.brand')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="brand"
                    autoComplete="on"
                    value={brandInput}
                    onChange={(e) => setBrandInput(e.target.value)}
                    placeholder={t('repaircalc.search.brandPlaceholder')}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t('repaircalc.search.model')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="model"
                    autoComplete="on"
                    value={modelInput}
                    onChange={(e) => setModelInput(e.target.value)}
                    placeholder={t('repaircalc.search.modelPlaceholder')}
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>

          {/* Preventivi esistenti per il modello */}
          {existingQuotes.length > 0 && !showCalculator && (
            <div className="repaircalc-section mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">{t('repaircalc.quotes.existingTitle')} — {brand} {model}</h5>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    setQuoteToEdit(null);
                    setShowCalculator(true);
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-1" />
                  {t('repaircalc.quotes.newQuote')}
                </Button>
              </div>

              {existingQuotes.map((quote) => (
                <Card key={quote.id} className="mb-3">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        {quote.repairType ? (
                          <div className="fw-semibold fs-5">{getRepairTypeLabel(quote.repairType, t)}</div>
                        ) : (
                          <div className="fw-semibold fs-5 text-muted fst-italic">{t('repaircalc.quotes.standardRepair')}</div>
                        )}
                        <div className="small text-muted mt-1">
                          <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                          {new Date(quote.updatedAt).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="d-flex gap-3 align-items-center">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleImportQuote(quote)}
                        >
                          <FontAwesomeIcon icon={faArrowRight} className="me-1" />
                          {t('repaircalc.calculator.importToDevice')}
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => {
                            setQuoteToEdit(quote);
                            setShowCalculator(true);
                          }}
                        >
                          <FontAwesomeIcon icon={faEdit} className="me-1" />
                          {t('repaircalc.quotes.update')}
                        </Button>
                        <div className="text-end">
                          <div className="fs-5 fw-bold text-profit">{formatCurrency(quote.totalClient, 'EUR')}</div>
                          <div className="small text-muted text-uppercase">{t('repaircalc.quotes.costClient')}</div>
                        </div>
                      </div>
                    </div>

                    <div className="row mt-3 g-2">
                      <div className="col-6 col-sm-3">
                        <div className="small text-muted text-uppercase">{t('repaircalc.calculator.partCostLabel')}</div>
                        <div className="fw-semibold">{formatCurrency(quote.partCost, 'EUR')}</div>
                      </div>
                      <div className="col-6 col-sm-3">
                        <div className="small text-muted text-uppercase">{t('repaircalc.calculator.difficulty')}</div>
                        <div className="fw-semibold text-capitalize">{difficultyLabel(quote.difficulty)}</div>
                      </div>
                      <div className="col-6 col-sm-3">
                        <div className="small text-muted text-uppercase">{t('repaircalc.calculator.paymentMethod')}</div>
                        <div className="fw-semibold">{quote.useCard ? t('repaircalc.calculator.card') : t('repaircalc.calculator.cash')}</div>
                      </div>
                      <div className="col-6 col-sm-3">
                        <div className="small text-muted text-uppercase">{t('repaircalc.archive.net')}</div>
                        <div className="fw-bold text-profit">{formatCurrency(quote.netProfit, 'EUR')}</div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}

          {/* Calcolatore */}
          {showCalculator && brand && model && (
            <Calculator
              key={quoteToEdit ? quoteToEdit.id : 'new'}
              settings={settings}
              onSave={handleSaveQuote}
              isSaving={isSaving}
              initialData={quoteToEdit}
              onImportToDevice={handleImportToDevice}
            />
          )}
        </div>

        {/* Inverso */}
        <div className={`tab-pane repaircalc-tab-wide ${currentTab === 'reverse' ? 'show active' : ''}`}>
          <ReverseCalculator settings={settings} />
        </div>

        {/* Archivio */}
        <div className={`tab-pane repaircalc-tab-wide ${currentTab === 'archive' ? 'show active' : ''}`}>
          <RepairCalcArchive
            onSelectQuote={(quote) => {
              setCurrentTab('calc');
              setBrandInput(quote.brand || '');
              setModelInput(quote.modelName || '');
            }}
            onImportQuote={handleImportQuote}
          />
        </div>

        {/* Impostazioni */}
        <div className={`tab-pane repaircalc-tab-narrow ${currentTab === 'settings' ? 'show active' : ''}`}>
          <SettingsView settings={settings} onChange={handleSettingsChange} />
        </div>
      </div>
    </Container>
  );
}
